import { useState } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Table2,
  Plus,
  Search,
  ArrowUpDown,
  ChevronDown,
  MoreHorizontal,
} from 'lucide-react';
import { Route as rootRoute } from './__root';
import { Button } from '../components/Button';
import { Pagination } from '../components/Pagination';
import {
  customersQueryOptions,
  type TenantRow,
} from '../lib/queries/customers';
import { useDebounce } from '../hooks/useDebounce';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: CustomerListPage,
});

const PAGE_SIZE = 10;

function formatTokens(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const PLAN_STYLE: Record<string, string> = {
  Enterprise: 'border-amber-500/50 text-amber-400',
  Growth: 'border-blue-500/50 text-blue-400',
  Starter: 'border-white/20 text-muted',
};

const STATUS_STYLE: Record<string, string> = {
  active: 'border-green-500/50 text-green-400',
  pending: 'border-yellow-500/50 text-yellow-400',
  suspended: 'border-red-500/50 text-red-400',
};

const STATUS_DOT: Record<string, string> = {
  active: 'bg-green-400',
  pending: 'bg-yellow-400',
  suspended: 'bg-red-400',
};

function StatCard({
  label,
  value,
  trend,
  loading,
  danger,
}: {
  label: string;
  value?: string | number | null;
  trend?: string;
  loading: boolean;
  danger?: boolean;
}) {
  return (
    <div className="bg-bg-elevated lgg:p-8 rounded-lg p-5">
      <p className="text-muted mb-3 text-xs font-medium tracking-wide uppercase md:text-sm">
        {label}
      </p>
      {loading ? (
        <div className="h-7 w-16 animate-pulse rounded bg-white/8" />
      ) : (
        <div className="flex flex-col items-baseline gap-0 md:flex-row md:gap-3 lg:flex-col lg:items-start lg:gap-1">
          <p
            className={[
              'flex text-[2rem] font-bold tabular-nums',
              danger ? 'text-red-400' : 'text-primary',
            ].join(' ')}
          >
            {value ?? '—'}
          </p>
          {trend && <p className="text-subtle text-xs">{trend}</p>}
        </div>
      )}
    </div>
  );
}

function TenantTable({
  tenants,
  loading,
}: {
  tenants: TenantRow[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-px p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-white/6" />
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Mobile card list */}
      <ul className="divide-primary/6 divide-y md:hidden">
        {tenants.length === 0 ? (
          <li className="text-muted px-4 py-10 text-center text-sm">
            No tenants match your search
          </li>
        ) : (
          tenants.map((t) => {
            const pct =
              t.tokenLimit > 0
                ? Math.min(100, (t.tokenUsage / t.tokenLimit) * 100)
                : 0;
            return (
              <li key={t.id} className="flex flex-col gap-3 px-4 py-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2">
                    <span
                      className={[
                        'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                        STATUS_DOT[t.status] ?? 'bg-white/40',
                      ].join(' ')}
                    />
                    <div className="flex flex-col gap-1">
                      <Link
                        to="/customers/$id"
                        params={{ id: t.id }}
                        className="text-primary truncate font-medium"
                      >
                        {t.name}
                      </Link>

                      <span
                        className={[
                          'w-fit rounded border px-2 py-0.5 text-xs font-medium',
                          PLAN_STYLE[t.plan] ?? 'text-muted border-white/20',
                        ].join(' ')}
                      >
                        {t.plan}
                      </span>
                    </div>
                  </div>
                  <span className="text-muted shrink-0 text-xs">
                    {formatRelativeTime(t.lastActivity)}
                  </span>
                </div>

                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-green-500 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })
        )}
      </ul>

      <table className="hidden w-full text-sm md:table">
        <thead>
          <tr className="border-b border-white/8">
            <th className="text-subtle px-4 py-3 text-left text-[0.625rem] uppercase">
              NAME
            </th>

            <th className="text-subtle hidden px-4 py-3 text-left text-[0.625rem] uppercase lg:table-cell">
              ID
            </th>

            <th className="text-subtle px-4 py-3 text-left text-[0.625rem] uppercase">
              PLAN
            </th>

            <th className="text-subtle px-4 py-3 text-left text-[0.625rem] uppercase">
              STATUS
            </th>

            <th className="text-subtle px-4 py-3 text-left text-[0.625rem] uppercase">
              TOKEN USAGE
            </th>

            <th className="text-subtle px-4 py-3 text-left text-[0.625rem] uppercase">
              LAST ACTIVITY
            </th>
          </tr>
        </thead>
        <tbody>
          {tenants.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-muted px-4 py-10 text-center">
                No tenants match your search
              </td>
            </tr>
          ) : (
            tenants.map((t) => {
              const pct =
                t.tokenLimit > 0
                  ? Math.min(100, (t.tokenUsage / t.tokenLimit) * 100)
                  : 0;
              return (
                <tr
                  key={t.id}
                  className="border-b border-white/6 transition-colors last:border-0 hover:bg-white/3"
                >
                  <td className="text-primary px-4 py-3 font-medium">
                    <Link to="/customers/$id" params={{ id: t.id }}>
                      {t.name}
                    </Link>
                  </td>
                  <td className="text-muted hidden px-4 py-3 font-mono text-xs lg:table-cell">
                    {t.id}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={[
                        'rounded border px-2 py-0.5 text-xs font-medium',
                        PLAN_STYLE[t.plan] ?? 'text-muted border-white/20',
                      ].join(' ')}
                    >
                      {t.plan}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={[
                        'rounded border px-2 py-0.5 text-xs font-medium capitalize',
                        STATUS_STYLE[t.status] ?? 'text-muted border-white/20',
                      ].join(' ')}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-1.5 w-28 overflow-hidden rounded-full bg-white/10">
                      <div
                        className="h-full rounded-full bg-green-500 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </td>
                  <td className="text-muted px-4 py-3 text-xs">
                    {formatRelativeTime(t.lastActivity)}
                  </td>
                  <td className="px-4 py-3">
                    <button className="text-muted hover:text-primary flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-white/6">
                      <MoreHorizontal size={15} />
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </>
  );
}

function CustomerListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [status, setStatus] = useState('');

  const { data, isLoading } = useQuery(
    customersQueryOptions({ page, limit: PAGE_SIZE, search, status }),
  );

  const stats = data?.stats;
  const tenants = data?.tenants ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const isEmpty = !isLoading && total === 0 && !search && !status;

  return (
    <div className="space-y-6 p-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total tenants"
          value={stats?.total}
          trend={
            stats?.totalDelta != null
              ? `↑${stats.totalDelta} this month`
              : undefined
          }
          loading={isLoading}
        />
        <StatCard
          label="Active"
          value={stats?.active}
          trend={
            stats?.activePct != null
              ? `${stats.activePct}% of total`
              : undefined
          }
          loading={isLoading}
        />
        <StatCard
          label="Needs attention"
          value={stats?.needsAttention}
          trend={
            stats?.attentionDelta != null
              ? `↑${stats.attentionDelta} since yesterday`
              : undefined
          }
          loading={isLoading}
          danger={(stats?.needsAttention ?? 0) > 0}
        />
        <StatCard
          label="Period tokens"
          value={formatTokens(stats?.periodTokens)}
          trend={
            stats?.tokenLimit != null
              ? `of ${formatTokens(stats.tokenLimit)} allocated`
              : undefined
          }
          loading={isLoading}
        />
      </div>

      {isEmpty ? (
        <div className="bg-bg-elevated flex flex-col items-center justify-center gap-4 rounded-lg border border-white/6 py-12 md:py-24">
          <Table2
            size={70}
            className="text-primary/8 hidden md:block"
            strokeWidth={2}
          />

          <Table2
            size={50}
            className="text-primary/8 block md:hidden"
            strokeWidth={2}
          />
          <div className="text-center">
            <p className="text-gray font-semibold">No tenants yet</p>
            <p className="text-gray mt-1 max-w-sm text-xs">
              You're in sandbox mode. Onboard your first tenant to get started
              and they'll appear here.
            </p>
          </div>
          <Link to="/customers/new" search={{ step: 1 }}>
            <Button className="py-6 text-sm font-semibold">
              <Plus size={22} />
              Onboard first tenant
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="hidden items-center justify-between py-3 lg:flex">
            <div className="relative max-w-72 flex-1">
              <Search
                size={14}
                className="text-primary/50 absolute top-1/2 left-3 -translate-y-1/2"
              />
              <input
                type="text"
                placeholder="Search tenants"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                className="text-primary placeholder:text-primary/12 border-primary/50 focus:ring-primary/50 w-full rounded border bg-transparent py-2.5 pr-3 pl-8 text-sm focus:ring-1 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-4">
              <button className="text-primary/50 hover:text-primary flex items-center gap-1.5 px-4 py-2.5 text-xs transition-colors hover:bg-white/4">
                <ArrowUpDown size={20} />
              </button>
              <button className="text-primary/50 hover:text-primary border-primary/50 flex items-center gap-1.5 rounded border px-4 py-2.5 text-xs transition-colors hover:bg-white/4">
                Filter
                <ChevronDown size={20} />
              </button>
            </div>
          </div>

          <div className="bg-bg-elevated md:border-primary/6 rounded-lg md:border">
            <TenantTable tenants={tenants} loading={isLoading} />
          </div>

          {!isLoading && total > 0 && (
            <div className="">
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
